import React, { memo, useCallback, useMemo, useState } from 'react'
import Modal from '@mui/material/Modal'
import tw from 'twin.macro'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { TeamMemberDetails, TeamRole } from 'src/interfaces/team'
import { Typography } from '@mui/material'
import { DataRow } from 'src/components/shared/DataRow'
import { TeamMemberStatusChip } from '../grid/TeamMemberStatusChip'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { AddMarkets } from 'src/components/markets/AddMarkets'
import { Market } from 'src/interfaces/market'
import { TeamMemberDetailsActions } from './TeamMemberDetailsActions'
import { SelectTeamRole } from '../select-team-role/SelectTeamRole'
import { TeamMemberStatus } from 'src/constants/team/team-members/enums'
import { useDispatch, useSelector } from 'react-redux'
import { UpdateTeamMemberAccess } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { getMarkets } from 'src/selectors/market'
import { getTeamRoles } from 'src/selectors/team'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'

interface Props {
  teamMemberDetails: TeamMemberDetails
  onClose(): void
  onRefresh(): Promise<void> | void
}

const TeamMemberDetailsModalComponent = ({ teamMemberDetails, onClose, onRefresh }: Props) => {
  const dispatch = useDispatch()

  const teamRoles = useSelector(getTeamRoles)
  const markets = useSelector(getMarkets)

  const { hasPermission } = useCheckPermissions()

  const container = useMemo(() => document.getElementById('root'), [])

  const initialSelectedMarkets = useMemo((): Market[] => {
    return teamMemberDetails.allMarkets ? markets : teamMemberDetails.markets || []
  }, [teamMemberDetails, markets])

  const [selectedMarkets, setSelectedMarkets] = useState<Market[]>(initialSelectedMarkets)
  const [openModalId, setOpenModalId] = useState<string | null>(null)

  const initialTeamRole =
    teamRoles?.find((role: TeamRole) => role.name === teamMemberDetails.role) || null

  const [selectedTeamRole, setSelectedTeamRole] = useState<TeamRole | null>(initialTeamRole)
  const [isRoleUpdating, setIsRoleUpdating] = useState(false)

  const allMarketsSelected = useMemo(
    () => selectedMarkets.length >= markets.length,
    [markets, selectedMarkets],
  )

  const handleSelectAllMarkets = useCallback(async () => {
    try {
      setSelectedMarkets(selectedMarkets)

      await createAsyncAction(
        dispatch,
        UpdateTeamMemberAccess.request({
          teamMemberId: teamMemberDetails.id,
          marketIds: markets.map((item) => item.id),
          allMarkets: true,
        }),
      )

      showToast('All markets added successfully!', { variant: 'success' })

      await onRefresh()
    } catch (err) {
      setSelectedMarkets(selectedMarkets)
      handleApiError(err)
    }
  }, [markets, teamMemberDetails.id])

  const handleSelectMarket = useCallback(
    async (newMarket: Market) => {
      try {
        const updatedMarkets = [...selectedMarkets, newMarket]
        const selectedMarkersIds = updatedMarkets.map((market) => market.id)
        const isAllMarkets = markets.length === updatedMarkets.length

        setSelectedMarkets(updatedMarkets)

        await createAsyncAction(
          dispatch,
          UpdateTeamMemberAccess.request({
            teamMemberId: teamMemberDetails.id,
            marketIds: selectedMarkersIds,
            allMarkets: isAllMarkets,
          }),
        )

        showToast('Market added successfully!', { variant: 'success' })

        await onRefresh()
      } catch (err) {
        setSelectedMarkets(selectedMarkets)
        handleApiError(err)
      }
    },
    [selectedMarkets, teamMemberDetails.id],
  )

  const handleRemoveMarket = useCallback(
    async (removedMarket: Market) => {
      try {
        const updatedMarkets = selectedMarkets.filter((market) => market.id !== removedMarket.id)
        const selectedMarketIds = updatedMarkets.map((market) => market.id)

        setSelectedMarkets(updatedMarkets)

        await createAsyncAction(
          dispatch,
          UpdateTeamMemberAccess.request({
            teamMemberId: teamMemberDetails.id,
            marketIds: selectedMarketIds,
            allMarkets: false,
          }),
        )

        showToast('Market removed successfully!', { variant: 'success' })

        await onRefresh()
      } catch (err) {
        setSelectedMarkets(selectedMarkets)
        handleApiError(err)
      }
    },
    [selectedMarkets, teamMemberDetails.id],
  )

  const handleTeamRoleSelect = useCallback(
    async (role: TeamRole) => {
      try {
        setIsRoleUpdating(true)
        setSelectedTeamRole(role)

        await createAsyncAction(
          dispatch,
          UpdateTeamMemberAccess.request({
            teamMemberId: teamMemberDetails.id,
            teamRoleId: role.id,
          }),
        )

        await onRefresh()

        setOpenModalId(null)
        showToast('Role updated successfully!', { variant: 'success' })
      } catch (err) {
        setSelectedTeamRole(selectedTeamRole)
        handleApiError(err)
      } finally {
        setIsRoleUpdating(false)
      }
    },
    [teamMemberDetails.id, selectedTeamRole],
  )

  return (
    <Modal
      container={container}
      open
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex flex-row w-full h-full`}
    >
      <>
        <div css={tw`w-[400px] h-full bg-white flex flex-col`}>
          <ModalHeader title="User details" onClose={onClose} className={tw`text-black h-[48px]`} />
          <div css={tw`max-w-[400px] flex-1 flex flex-col justify-start border-0 p-4`}>
            <Typography
              css={tw`pb-4 border-0 border-b border-solid border-b-[#EDEDED]`}
              variant="h2"
            >{`${teamMemberDetails.firstName} ${teamMemberDetails.lastName}`}</Typography>
            <DataRow
              title="Status"
              direction="row"
              component={
                <TeamMemberStatusChip
                  status={
                    teamMemberDetails.disabled
                      ? TeamMemberStatus.Deactivated
                      : TeamMemberStatus.Active
                  }
                />
              }
            />
            <DataRow direction="row" title="Email" text={teamMemberDetails.email} />
            <div css={tw`py-4 border-0 border-b border-solid border-[#EDEDED]`}>
              <SelectTeamRole
                selectedTeamRole={selectedTeamRole}
                handleTeamRoleSelect={handleTeamRoleSelect}
                modalId="team-details"
                openModalId={openModalId}
                setOpenModalId={setOpenModalId}
                isLoading={isRoleUpdating}
                disabled={!hasPermission(UserPermission.AccessUpdate)}
              />
            </div>
            <AddMarkets
              markets={selectedMarkets}
              className={tw`py-[14px] border-0 border-b border-solid border-b-[#EDEDED]`}
              onSelectMarket={handleSelectMarket}
              onRemoveMarket={handleRemoveMarket}
              onSelectAllMarkets={handleSelectAllMarkets}
              allMarketsSelected={allMarketsSelected}
              disabled={!hasPermission(UserPermission.AccessUpdate)}
            />
          </div>
          {hasPermission(UserPermission.UsersEdit) ? (
            <TeamMemberDetailsActions
              teamMemberDetails={teamMemberDetails}
              onClose={onClose}
              onRefresh={onRefresh}
            />
          ) : null}
        </div>
        <div id="team-details" css={tw`flex m-6 w-[440px] pointer-events-none`} />
      </>
    </Modal>
  )
}

export const TeamMemberDetailsModal = memo(TeamMemberDetailsModalComponent)
